const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const FLEET_SIDEBAR_DEFAULT_WIDTH = 350;
export const FLEET_SIDEBAR_MIN_WIDTH = 290;
export const FLEET_SIDEBAR_MAX_WIDTH = 620;
export const FLEET_SIDEBAR_DEFAULT_HEIGHT = 340;
export const FLEET_SIDEBAR_MIN_HEIGHT = 200;
export const FLEET_SIDEBAR_MAX_HEIGHT = 700;

export const clampFleetSidebarWidth = (width, viewportWidth) => {
  const requestedWidth = Number(width);
  const availableWidth = Number(viewportWidth);
  const viewportMaximum = Number.isFinite(availableWidth)
    ? availableWidth - 48
    : FLEET_SIDEBAR_MAX_WIDTH;
  const maximum = Math.max(
    FLEET_SIDEBAR_MIN_WIDTH,
    Math.min(FLEET_SIDEBAR_MAX_WIDTH, viewportMaximum),
  );

  if (!Number.isFinite(requestedWidth)) return FLEET_SIDEBAR_DEFAULT_WIDTH;

  return Math.min(maximum, Math.max(FLEET_SIDEBAR_MIN_WIDTH, requestedWidth));
};

export const clampFleetSidebarHeight = (height, workspaceHeight) => {
  const requestedHeight = Number(height);
  const availableHeight = Number(workspaceHeight);
  const workspaceMaximum = Number.isFinite(availableHeight)
    ? availableHeight - 72
    : FLEET_SIDEBAR_MAX_HEIGHT;
  const maximum = Math.max(
    160,
    Math.min(FLEET_SIDEBAR_MAX_HEIGHT, workspaceMaximum),
  );
  const minimum = Math.min(FLEET_SIDEBAR_MIN_HEIGHT, maximum);

  if (!Number.isFinite(requestedHeight)) {
    return Math.min(FLEET_SIDEBAR_DEFAULT_HEIGHT, maximum);
  }

  return Math.min(maximum, Math.max(minimum, requestedHeight));
};

export const toMapPosition = (value) => {
  if (!value) return null;

  const latitude = asNumber(value.latitude ?? value.lat);
  const longitude = asNumber(value.longitude ?? value.lng ?? value.long);

  if (
    latitude === null ||
    longitude === null ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }

  return { lat: latitude, lng: longitude };
};

export const normalizeDriverLocations = (locations = []) =>
  locations.reduce((normalized, location) => {
    const driverId = location?.driver_id ?? location?.driver?.id;
    const position = toMapPosition(location);

    if (
      driverId === null ||
      driverId === undefined ||
      driverId === '' ||
      !position
    ) {
      return normalized;
    }

    normalized[String(driverId)] = {
      ...position,
      updatedAt: location?.updated_at || location?.updatedAt || null,
    };

    return normalized;
  }, {});

const getLocationTimestamp = (location) => {
  if (!location?.updatedAt) return null;

  const timestamp =
    typeof location.updatedAt === 'number'
      ? location.updatedAt
      : new Date(location.updatedAt).getTime();

  return Number.isFinite(timestamp) ? timestamp : null;
};

export const mergeDriverLocations = (current = {}, incoming = {}) =>
  Object.entries(incoming).reduce(
    (merged, [driverId, location]) => {
      const existing = merged[driverId];
      const existingTimestamp = getLocationTimestamp(existing);
      const incomingTimestamp = getLocationTimestamp(location);

      if (
        !existing ||
        existingTimestamp === null ||
        (incomingTimestamp !== null && incomingTimestamp >= existingTimestamp)
      ) {
        merged[driverId] = location;
      }

      return merged;
    },
    { ...current },
  );

export const getDriverInitials = (name = '') => {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return initials || '?';
};

export const getDriverTruckTypes = (driver) => {
  const sources = [
    driver?.truck_type,
    ...(driver?.truck_types || []),
    ...(driver?.vehicles || []).map(
      (vehicle) => vehicle?.truck_type || vehicle?.truckType,
    ),
  ];

  return Array.from(
    sources.reduce((types, truckType) => {
      if (!truckType) return types;

      const key = String(truckType.id ?? truckType.title ?? truckType.type ?? '');
      if (key) {
        types.set(key, {
          ...(types.get(key) || {}),
          ...truckType,
        });
      }
      return types;
    }, new Map()).values(),
  );
};

export const driverMatchesTruckType = (truckTypes = [], truckTypeId = '') =>
  !truckTypeId ||
  truckTypes.some((truckType) => String(truckType?.id) === String(truckTypeId));

export const hasActiveDelivery = (driver) =>
  driver?.has_active_delivery === true ||
  Number(driver?.has_active_delivery) === 1;

export const isLocationFresh = (
  location,
  now = Date.now(),
  thresholdMs = 2 * 60 * 1000,
) => {
  const timestamp = getLocationTimestamp(location);
  return timestamp !== null && now - timestamp <= thresholdMs;
};

export const getLocationStatus = (location, now = Date.now()) => {
  if (!location) return 'no-location';
  return isLocationFresh(location, now) ? 'live' : 'stale';
};

export const formatRelativeTime = (value, now = Date.now()) => {
  if (!value) return 'Awaiting location';

  const timestamp =
    typeof value === 'number' ? value : new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Awaiting location';

  const seconds = Math.max(0, Math.floor((now - timestamp) / 1000));
  if (seconds < 20) return 'Just now';
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.floor(hours / 24)}d ago`;
};
